import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  inject,
  signal,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import {
  FormBuilder,
  FormControl,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatCheckboxModule } from '@angular/material/checkbox';
import {
  MAT_DIALOG_DATA,
  MatDialogModule,
  MatDialogRef,
} from '@angular/material/dialog';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { TranslatePipe } from '@ngx-translate/core';
import { Auth } from '@angular/fire/auth';
import { firstValueFrom } from 'rxjs';
import { FieldAdvisoryComponent } from '@shared/components/field-advisory/field-advisory.component';
import { UserService } from '@core/services/auth/user.service';
import { AuthStore } from '@core/services/auth/auth.store';
import { User } from '@shared/definitions/user';

export interface WhatsappConnectDialogData {
  /** Prefill when the profile already has a number but no WhatsApp opt-in. */
  initialPhone?: string;
}

/**
 * Collect phone + WhatsApp consent from the email-verification banner.
 *
 * Writing `profile.phone` + `profile.whatsappOptIn: true` fires
 * `onMemberWhatsappActivated`, which sends the confirmation link. The live
 * `users/{uid}` snapshot in AuthService then flips the banner into the
 * existing « resend WhatsApp » state — no manual store patch required.
 */
@Component({
  selector: 'app-whatsapp-connect-dialog',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    ReactiveFormsModule,
    MatButtonModule,
    MatCheckboxModule,
    MatDialogModule,
    MatFormFieldModule,
    MatIconModule,
    MatInputModule,
    MatProgressSpinnerModule,
    TranslatePipe,
    FieldAdvisoryComponent,
  ],
  template: `
    <h2 mat-dialog-title class="!text-lg !font-semibold !mb-1 flex items-center gap-2">
      <mat-icon class="text-amber-500 !w-5 !h-5 !text-xl">chat</mat-icon>
      {{ 'shared.emailVerification.whatsappConnect.title' | translate }}
    </h2>

    <mat-dialog-content class="!pt-2">
      <form [formGroup]="form" (ngSubmit)="submit()">
        <p class="text-sm text-secondary !mb-4">
          {{ 'shared.emailVerification.whatsappConnect.message' | translate }}
        </p>

        <mat-form-field appearance="outline" class="w-full">
          <mat-label>
            {{ 'shared.emailVerification.whatsappConnect.phoneLabel' | translate }}
          </mat-label>
          <input
            matInput
            type="tel"
            inputmode="tel"
            autocomplete="tel"
            formControlName="phone"
            [placeholder]="'shared.emailVerification.whatsappConnect.phonePlaceholder' | translate"
          />
          @if (form.controls.phone.touched && form.controls.phone.hasError('required')) {
            <mat-error>
              {{ 'shared.emailVerification.whatsappConnect.phoneRequired' | translate }}
            </mat-error>
          }
        </mat-form-field>
        <app-field-advisory
          kind="phone"
          [value]="form.controls.phone.value"
          [touched]="form.controls.phone.touched"
        ></app-field-advisory>

        <mat-checkbox formControlName="whatsappOptIn" class="!mt-3 block">
          {{ 'shared.emailVerification.whatsappConnect.optIn' | translate }}
        </mat-checkbox>
        @if (form.controls.whatsappOptIn.disabled) {
          <p class="text-xs text-secondary !mt-1 !mb-0">
            {{ 'shared.emailVerification.whatsappConnect.optInNeedsPhone' | translate }}
          </p>
        }

        @if (saveError()) {
          <p class="text-sm text-red-600 flex items-center gap-1 !mt-3">
            <mat-icon class="!w-4 !h-4 !text-base">error_outline</mat-icon>
            {{ 'shared.emailVerification.whatsappConnect.saveFailed' | translate }}
          </p>
        }
      </form>
    </mat-dialog-content>

    <mat-dialog-actions align="end" class="!px-6 !pb-4 gap-2">
      <button mat-button type="button" [disabled]="saving()" (click)="cancel()">
        {{ 'shared.actions.cancel' | translate }}
      </button>
      <button
        mat-flat-button
        color="primary"
        type="button"
        [disabled]="!canSubmit() || saving()"
        (click)="submit()"
      >
        @if (saving()) {
          <mat-spinner diameter="18" class="!inline-block !align-middle !mr-2"></mat-spinner>
          {{ 'shared.emailVerification.whatsappConnect.saving' | translate }}
        } @else {
          {{ 'shared.emailVerification.whatsappConnect.submit' | translate }}
        }
      </button>
    </mat-dialog-actions>
  `,
})
export class WhatsappConnectDialogComponent {
  readonly data = inject<WhatsappConnectDialogData>(MAT_DIALOG_DATA, {
    optional: true,
  });
  private readonly dialogRef = inject(
    MatDialogRef<WhatsappConnectDialogComponent, boolean>,
  );
  private readonly fb = inject(FormBuilder);
  private readonly userService = inject(UserService);
  private readonly authStore = inject(AuthStore);
  private readonly auth = inject(Auth);
  private readonly destroyRef = inject(DestroyRef);

  readonly saving = signal(false);
  readonly saveError = signal(false);

  readonly form = this.fb.group({
    phone: new FormControl(this.data?.initialPhone?.trim() ?? '', {
      nonNullable: true,
      validators: [Validators.required],
    }),
    whatsappOptIn: this.fb.control(true, { nonNullable: true }),
  });

  constructor() {
    this.syncOptInAvailability();
    this.form.controls.phone.valueChanges
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => this.syncOptInAvailability());
  }

  canSubmit(): boolean {
    const phone = this.form.controls.phone.value.trim();
    return !!phone && !!this.form.controls.whatsappOptIn.value;
  }

  submit(): void {
    this.form.markAllAsTouched();
    if (!this.canSubmit() || this.saving()) return;

    const uid = this.auth.currentUser?.uid ?? this.authStore.user()?.userUID;
    if (!uid) {
      this.saveError.set(true);
      return;
    }

    const phone = this.form.controls.phone.value.trim();
    this.saving.set(true);
    this.saveError.set(false);

    void firstValueFrom(
      this.userService.updateUser(uid, {
        'profile.phone': phone,
        'profile.whatsappOptIn': true,
      } as unknown as Partial<User>),
    )
      .then(() => this.dialogRef.close(true))
      .catch(() => {
        this.saving.set(false);
        this.saveError.set(true);
      });
  }

  cancel(): void {
    if (this.saving()) return;
    this.dialogRef.close(false);
  }

  /**
   * Same rule as onboarding: no number → no consent that cannot be honoured.
   * Driven on the control (not a template `[disabled]`) for reactive forms.
   */
  private syncOptInAvailability(): void {
    const ctrl = this.form.controls.whatsappOptIn;
    const phone = this.form.controls.phone.value.trim();
    if (phone) {
      if (ctrl.disabled) ctrl.enable({ emitEvent: false });
      return;
    }
    if (ctrl.value) ctrl.setValue(false, { emitEvent: false });
    if (ctrl.enabled) ctrl.disable({ emitEvent: false });
  }
}
